/**
 * (Client twin of server/src/lib/documentPages.ts — keep the two identical;
 * S/documentPages.test.ts runs both over the same fixtures.)
 *
 * Which pages a document's parts hold after its PDFs change.
 *
 * An uploaded PDF arrives as its PAGES: one `template` part per page, at the
 * end of the list, so each can be moved on its own and a blank page or the
 * answers put between any two (Ben, 2026-09-28: "an uploaded template breaks
 * itself into the individual pages"). Pure, and run on both sides with the
 * same inputs — the route on the stored parts, the Document tab on the parts
 * on screen — which is what lets an upload save at once and still leave the
 * screen agreeing with what was stored.
 */

/** Just enough of a PDF: which one, and how many pages it has. */
export interface PdfRef { id?: string; pages: number }

/** Just enough of a part. Everything else on it is carried through untouched. */
export type PartLike = { type: string; templateId?: string; pages?: number[] } & Record<string, unknown>;

/** A part per page of one PDF, in order. */
export function pagePartsOf(pdf: PdfRef): PartLike[] {
  return Array.from({ length: Math.max(0, pdf.pages) }, (_, i) => ({
    type: 'template', templateId: pdf.id, pages: [i + 1],
  }));
}

/**
 * The parts, after the PDFs went from `before` to `after`.
 *
 * - Every `template` part NAMES its PDF on the way out; one that named none
 *   meant the first of `before`.
 * - A PDF that is gone takes its parts with it.
 * - A PDF whose page count changed (a Replace, a blank page grown or cut):
 *   each part keeps the pages that still exist, and one left with none goes.
 *   Pages it GAINED are inserted as parts of their own after its last part —
 *   unless a part takes the whole PDF, which already brings them.
 * - A PDF new in `after` is appended as its pages. On a document with no
 *   parts — "every PDF, whole, in order" — the PDFs already there are split
 *   into their pages first, so the new one lands after them, not instead.
 */
export function arrangePages<T extends PartLike>(
  blocks: T[] | undefined, before: PdfRef[], after: PdfRef[]
): PartLike[] {
  const was = new Map(before.map((t) => [t.id, t.pages]));
  const now = new Map(after.map((t) => [t.id, t.pages]));
  const first = before[0]?.id ?? after[0]?.id;
  const added = after.filter((t) => !was.has(t.id));

  let parts: PartLike[] = (blocks || []).map((b) => (
    b.type === 'template' && !b.templateId && first ? { ...b, templateId: first } : b
  ));

  // With nothing arranged the PDFs are the document; the first change that
  // adds one makes the arrangement explicit, in the order it already had.
  if (!parts.length && added.length) {
    parts = before.filter((t) => now.has(t.id)).flatMap((t) => pagePartsOf({ id: t.id, pages: now.get(t.id) as number }));
  }

  const out: PartLike[] = [];
  for (const part of parts) {
    if (part.type !== 'template' || !part.templateId) { out.push(part); continue; }
    const count = now.get(part.templateId);
    if (count === undefined) continue;                        // its PDF is gone
    if (!part.pages?.length) { out.push(part); continue; }    // the whole PDF, whatever its length
    const pages = part.pages.filter((p) => p <= count);
    if (pages.length) out.push(pages.length === part.pages.length ? part : { ...part, pages });
  }

  // Pages a PDF gained, after its last part.
  for (const t of after) {
    const had = was.get(t.id);
    if (had === undefined || t.pages <= had) continue;
    const mine = out.filter((p) => p.type === 'template' && p.templateId === t.id);
    if (!mine.length || mine.some((p) => !p.pages?.length)) continue;
    const at = out.lastIndexOf(mine[mine.length - 1]) + 1;
    const gained = pagePartsOf({ id: t.id, pages: t.pages }).slice(had);
    out.splice(at, 0, ...gained);
  }

  for (const t of added) out.push(...pagePartsOf(t));
  return out;
}
